/*
 * Persistence (Weather window)
 */
import { Button, Col, DatePicker, Input, Layout, Row, Space, Spin, Typography } from 'antd';
import { InputNumber, Select } from 'antd';
import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useContext,
  ChangeEvent,
  useMemo,
  ReactNode,
} from 'react';
import { isEmpty } from 'underscore';
import { Label } from '../../../../components/Label/Label';
import { IPlotVariable } from '../../../../typings/analytics';
import styles from '../../Style/LayoutPanel.module.scss';
import { ChartBox, AnalyticsDownloadLinks } from '../ChartBox';
import { IBestDataset } from '../../../../typings/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import moment, { Moment } from 'moment';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import Api from '../../../../api/Api';
import { StoreContext } from '../../../../store/store';
import IconPoint from '../../../../assets/icon/locate2.png'
import CustomDivider from '../CustomDIvider';
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function Persistence(props: {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[]
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  persistenceImage: string;
  persistenceLinks: string[];
  updating: boolean;
  error: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  onDatasetChange: (item: string) => void
  gridCellSize: string
  waterDepth: string
  point?: MarkerItem
  listNavigation: ReactNode
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
}) {
  const {
    dateRange,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    onUpdate,
    error,
    persistenceImage,
    persistenceLinks,
    pointName,
    userInput,
    updateUserInput,
    updating,
    onDatasetChange,
    categoryDataset,
    gridCellSize,
    waterDepth,
    listNavigation,
    point,
    onChangeActiveKey,
    pointOption
  } = props;

  const context = useContext(StoreContext)

  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [variablesY, setVariablesY] = useState<IPlotVariable[] | null>([]);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.Persistence.variableX
  );
  const [variableY, setVariableY] = useState<IPlotVariable | null>(
    userInput.Persistence.variableY
  );
  const [dataset, setDataset] = useState(
    userInput.Persistence.datasetX || previewDatasetId
  );
  const [dataset2, setDataset2] = useState("");
  const [duration, setDuration] = useState(userInput.Persistence.duration);
  const [sign, setSign] = useState<'below' | 'above'>(
    userInput.Persistence.sign
  );
  const [sign2, setSign2] = useState<'below' | 'above'>(
    userInput.Persistence.sign2
  );
  const [percentile, setPercentile] = useState(
    userInput.Persistence.percentile
  );
  const [threshold, setThreshold] = useState(
    userInput.Persistence.threshold || ''
  );
  const [threshold2, setThreshold2] = useState(
    userInput.Persistence.threshold2 || ''
  );
  const [conditionalDSSelected, setConditionalDSSelected] = useState(
    userInput.Persistence.conditionalDSSelected
  );

  const [IsChangeDatasetConditional, setIsChangeDatasetConditional] = useState(false)

  const [startDate, setStartDate] = useState(
    userInput.Persistence.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.Persistence.endDate || moment(dateRange[1])
  );

  useEffect(() => {
    updateUserInput({
      Persistence: {
        datasetX: dataset,
        datasetY: dataset2,
        variableX: variableX,
        variableY: variableY,
        sign: sign,
        sign2: sign2,
        percentile: percentile,
        duration: duration,
        threshold: threshold,
        threshold2: threshold2,
        startDate: startDate,
        endDate: endDate,
        conditionalDSSelected: conditionalDSSelected,
      },
    });
  }, [
    dataset,
    dataset2,
    variableX,
    variableY,
    sign,
    sign2,
    percentile,
    duration,
    threshold,
    threshold2,
    startDate,
    endDate,
    conditionalDSSelected,
  ]);

  const getPersistenceImage = useCallback(
    (
      datasetId: string,
      datasetId2: string,
      thresh: string,
      thresh2: string,
      indexX: IPlotVariable,
      indexY: IPlotVariable | null,
      orderId: string,
      orderId2: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW,
          Thresholds: thresh,
          Duration: duration.toString(),
          DatasetId: datasetId,
          Percentile: percentile,
          Sign1: sign,
          OriginalDatasetId: datasetId,
          enableConditional: datasetId2 !== "",
          OrderId2: orderId2,
          DatasetId2: datasetId2,
          AcronymY: indexY != null ? indexY.ShortName ? indexY.ShortName : indexY.Name : '',
          TitleY: indexY?.Name ?? '',
          ColumnIndexY: indexY?.Index ?? 0,
          Thresholds2: thresh2,
          Sign2: sign2,
          IntervalCountY: 16,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, duration, sign, sign2, percentile, pointName]
  );

  useEffect(() => {
    if (previewDatasetId && !isEmpty(categoryDataset) && !isEmpty(coordinates) && persistenceImage === '') {
      setDataset(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === props.coordinates.join()
        );
        if (task) {
          const itemsInfos = task.ItemsInfo.filter(
            (itemInfo: IPlotVariable) => itemInfo.Name !== 'Time'
          );
          if (itemsInfos.length > 1) {
            setVariablesX(itemsInfos);
            const indexX = itemsInfos[0];
            setVariableX(indexX);
            getPersistenceImage(
              dataset,
              '',
              '',
              '',
              indexX,
              null,
              task.OrderId,
              '',
              startDate.format(DT0Format),
              endDate.format(DT0Format)
            );
          }
        }
      }
    }
  }, [previewDatasetId, categoryDataset, enrichedMetadata, coordinates, persistenceImage]);

  const onUpdatePersistence = useCallback(() => {
    if (enrichedMetadata && variableX && dataset) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === dataset &&
          task.CoordinatesString === props.coordinates.join()
      );
      if (dataset && task) {
        let orderId2 = task.OrderId;
        if (dataset !== dataset2 && dataset2 != '') {
          const task2 = enrichedMetadata.find(
            (task: IEnrichedMetadata) =>
              task.DatasetId === dataset2 &&
              task.CoordinatesString === props.coordinates.join()
          );
          if (task2) {
            orderId2 = task2.OrderId;
          }
        }
        getPersistenceImage(
          dataset,
          dataset2,
          threshold,
          threshold2,
          variableX,
          variableY,
          task.OrderId,
          orderId2,
          startDate.format(DT0Format),
          endDate.format(DT0Format)
        );
      }
    }
  }, [
    enrichedMetadata,
    variableX,
    variableY,
    dataset,
    dataset2,
    threshold,
    threshold2,
    duration,
    sign,
    sign2,
    percentile,
    startDate,
    endDate,
    pointName
  ]);

  function doVariableChange(active: number) {
    const item = variablesX.find((a) => a.Index === active)!;
    setVariableX(item);
  }

  function doVariable2Change(active: number) {
    const item = variablesY?.find((a) => a.Index === active)!;
    setVariableY(item);
  }

  function onChangeDuration(value: number | string | null | undefined) {
    const MAX_HOURS = 336;
    if (value !== undefined && value !== null && !isNaN(Number(value))) {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        if (numValue > MAX_HOURS) {
          setDuration(MAX_HOURS);
        } else {
          setDuration(numValue);
        }
      }else {
        setDuration(1)
      }
    }
  }

  function doSignChange(value: 'above' | 'below') {
    setSign(value);
  }

  function doPercentileChange(value: number) {
    setPercentile(value);
  }

  function doSign2Change(value: 'above' | 'below') {
    setSign2(value);
  }

  async function doDataset2Change(value: string) {
    if (value != '') {
      if (!enrichedMetadata) return

      let metadata = enrichedMetadata.find(
        (md: IEnrichedMetadata) =>
          md.DatasetId === value &&
          md.CoordinatesString === coordinates.join()
      );

      if (!metadata) {
        try {
          setIsChangeDatasetConditional(true)
          metadata = await Api.metadata.getMetadata(value);
          const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
            [metadata!],
            coordinates,
            pointName,
            gridCellSize,
            waterDepth,
            context.datasetBriefs,
            context.fwePoints,
          );
          context.setEnrichedMetadata(createFullExtractionTasks);
        } catch (err) {
          console.log(err)
        } finally {
          setIsChangeDatasetConditional(false)
        }
      }

      if (!metadata) return

      const withoutTime = metadata.ItemsInfo.filter(
        (item: IPlotVariable) => item.Name !== 'Time'
      );
      setVariablesY(withoutTime);
      setVariableY(withoutTime.length > 0 ? withoutTime[0] : null);
      setConditionalDSSelected(true);
    } else {
      setVariablesY(null);
      setVariableY(null);
      setConditionalDSSelected(false);
    }
    setDataset2(value);
  }

  function threshhold1Isvalid(threshold1: string) {
    const numbers = threshold1.split(',');
    if (numbers.length > 0 && numbers.length < 7) {
      const validNumbers = numbers.filter(
        (value: string) => value !== undefined && !isNaN(Number(value))
      );
      if (numbers.length === validNumbers.length) {
        return true;
      }
    }
    return false;
  }

  function onChangeThreshold(e: ChangeEvent<HTMLInputElement>) {
    if (threshhold1Isvalid(e.target.value)) {
      setThreshold(e.target.value);
    }
  }

  function onChangeThreshold2(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    if (value !== undefined && !isNaN(Number(value))) {
      setThreshold2(value);
    }
  }

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onSelectDataset = (value: string) => {
    onDatasetChange(value)
  }

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day');
  }

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [categoryDataset])

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={onChangeActiveKey}
                options={pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  onChange={onSelectDataset}
                  disabled={updating}
                >
                   {categories.map((category: string) => {
                      const section = category
                      return (
                        <Select.OptGroup key={category} label={section}>
                          {categoryDataset
                            .filter((item) => {
                              return item.label === category;
                            })
                            .map((item) => {
                              return (
                                <Select.Option key={item.id} value={item.id}>
                                  {item.value}
                                </Select.Option>
                              );
                            })}
                        </Select.OptGroup>
                      );
                    })}
                </Select>
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  placeholder="Select variable"
                  onChange={doVariableChange}
                  value={variableX?.Index ? variableX.Index : 0}
                  disabled={updating}
                >
                  {variablesX.map((item) => {
                    return (
                      <Select.Option key={item.Index} value={item.Index}>
                        {item.Name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              disabled={updating}
              loading={updating}
              onClick={onUpdatePersistence}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
        {!updating && error && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{error}</div>}
        {!updating && !error && (
           <ChartBox
            actionRow={<></>}
            key={'Persistence (Weather windows)'}
            title={'Persistence (Weather windows)'}
            height={updating || !isEmpty(error) ? '50px' : 'auto'}
            plot={
              updating ? (
                <div className={styles.spinner}>
                  <Spin size="large" />
                </div>
              ) : !isEmpty(error) ? (
                <div>{error}</div>
              ) : persistenceImage && (
                <img
                  style={{ width: '80%', objectFit: 'scale-down' }}
                  src={persistenceImage}
                  alt="Persistence"
                />
              )
            }
            dataset={''}
            link={persistenceLinks}
          />
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
            maxHeight: '900px',
            overflowY: 'auto'
          }}
        >
          <Label label="Sign" className={styles.actionItem}>
            <Select
              value={sign}
              style={{ width: '100%' }}
              onChange={doSignChange}
              disabled={updating}
            >
              <Select.Option value="below">Below</Select.Option>
              <Select.Option value="above">Above</Select.Option>
            </Select>
          </Label>
          <Label label="Percentile" className={styles.actionItem}>
            <Select
              value={percentile}
              style={{ width: '100%' }}
              onChange={doPercentileChange}
              disabled={updating}
            >
              {[5, 10, 20, 25, 50, 75, 80, 90, 95].map((item, index) => {
                return (
                  <Select.Option key={index} value={item}>
                    {item}
                  </Select.Option>
                );
              })}
            </Select>
          </Label>
          <Label label="Duration [hours]" className={styles.actionItem}>
            <InputNumber
              value={duration}
              onChange={onChangeDuration}
              disabled={updating}
              style={{ width: '100%' }}
            />
          </Label>
          <Label
            label="Threshold(s) (up to 6 values, comma separated)"
            className={styles.actionItem}
          >
            <Input
              style={{ width: '100%' }}
              value={threshold}
              onChange={onChangeThreshold}
            />
          </Label>
          <Label label="Conditional Dataset" className={styles.actionItem}>
            <Select
              value={dataset2}
              style={{ width: '100%' }}
              onChange={doDataset2Change}
              disabled={IsChangeDatasetConditional || updating}
            >
              <Select.Option key="none" value="">
                None
              </Select.Option>
              {categories.map((category: string) => {
                const section = category
                return (
                  <Select.OptGroup key={category} label={section}>
                    {categoryDataset
                      .filter((item) => {
                        return item.label === category;
                      })
                      .map((item) => {
                        return (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        );
                      })}
                  </Select.OptGroup>
                );
              })}
            </Select>
          </Label>
          {conditionalDSSelected && (
            <>
              <Label label="Sign" className={styles.actionItem}>
                <Select
                  value={sign2}
                  style={{ width: '100%' }}
                  onChange={doSign2Change}
                  disabled={updating}
                >
                  <Select.Option value="below">Below</Select.Option>
                  <Select.Option value="above">Above</Select.Option>
                </Select>
              </Label>
              <Label label="Threshold (1 value)" className={styles.actionItem}>
                <Input
                  disabled={updating}
                  value={threshold2}
                  onChange={onChangeThreshold2}
                />
              </Label>
            </>
          )}
        </Sider>
      </Layout>
    </Layout>
  );
}
